"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Karta 5 — „Wyceny z aktualną ceną produktów"
 * Mini-formularz „Nowa usługa" — produkty pojawiają się sekwencyjnie,
 * total liczy się w czasie rzeczywistym (count-up animation)
 */
const PRODUCTS = [
  { name: "Anestetyk · 1 amp.", price: 28.5 },
  { name: "Strzykawka 5 ml · 2 szt.", price: 4.2 },
  { name: "Bandage · 1 szt.", price: 12.8 },
];

const MARKUP_PERCENT = 30;
const PRODUCTS_SUBTOTAL = PRODUCTS.reduce((s, p) => s + p.price, 0); // 45,50
const MARKUP_AMOUNT = (PRODUCTS_SUBTOTAL * MARKUP_PERCENT) / 100; // 13,65
const FINAL_TOTAL = PRODUCTS_SUBTOTAL + MARKUP_AMOUNT; // 59,15

// Total targets per step:
// 0 = empty, 1 = +Anestetyk, 2 = +Strzykawka, 3 = +Bandage (subtotal),
// 4 = +narzut (final total animuje), 5 = idle, 6 = reset
const TOTAL_TARGETS = [0, 28.5, 32.7, PRODUCTS_SUBTOTAL, FINAL_TOTAL, FINAL_TOTAL, 0];

function formatPLN(value: number): string {
  return value.toFixed(2).replace(".", ",") + " zł";
}

function useCountUp(target: number, duration = 600): string {
  const [value, setValue] = useState(target);

  useEffect(() => {
    const start = value;
    const startTime = performance.now();
    let raf = 0;

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(start + (target - start) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return formatPLN(value);
}

export function VisualPricing() {
  // 0 = puste, 1-3 = produkty, 4 = +narzut, 5 = idle z pełnym totalem, 6 = reset
  const [step, setStep] = useState(0);

  useEffect(() => {
    const intervals = [800, 1700, 1700, 1700, 2200, 1800, 800];
    const t = setTimeout(() => {
      setStep((s) => (s + 1) % intervals.length);
    }, intervals[step]);
    return () => clearTimeout(t);
  }, [step]);

  // Liczba widocznych produktów: 1-4 → 1,2,3,3, w resecie (6) → 0
  const visibleCount = step === 0 || step === 6 ? 0 : Math.min(step, 3);
  const showMarkup = step === 4 || step === 5;
  const totalTarget = TOTAL_TARGETS[step];
  const totalDisplay = useCountUp(totalTarget);

  return (
    <div
      className="absolute inset-x-4 bottom-4 top-2 pointer-events-none"
      aria-hidden
    >
      <div className="w-full h-full rounded-xl bg-card border border-border/60 shadow-sm overflow-hidden flex flex-col">
        {/* Header — section label + service name */}
        <div className="px-3 py-2 border-b border-border/40">
          <div className="text-[8px] font-semibold uppercase tracking-wider text-primary mb-0.5">
            Nowa usługa
          </div>
          <div className="text-[11px] font-semibold text-foreground">
            Sterylizacja kotki
          </div>
        </div>

        {/* Product list — fixed height, items animate in */}
        <div className="flex-1 px-3 py-2 flex flex-col">
          <AnimatePresence>
            {PRODUCTS.slice(0, visibleCount).map((product, i) => (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{
                  duration: 0.25,
                  ease: [0.21, 0.47, 0.32, 0.98],
                }}
                className="flex items-center justify-between text-[10px] py-1.5 border-b border-border/30"
              >
                <span className="text-muted-foreground truncate pr-2">
                  {product.name}
                </span>
                <span className="font-medium text-foreground tabular-nums flex-shrink-0">
                  {formatPLN(product.price)}
                </span>
                {/* Subtle blue flash when first appears */}
                {i === visibleCount - 1 && (
                  <motion.div
                    className="absolute inset-x-3 h-[24px] -ml-3 rounded bg-primary/[0.06] pointer-events-none"
                    initial={{ opacity: 0.4 }}
                    animate={{ opacity: 0 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                  />
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Empty state placeholder */}
          {visibleCount === 0 && (
            <div className="text-[9px] text-muted-foreground/50 italic mt-2">
              Dodaj produkty…
            </div>
          )}

          {/* Narzut 30% — pojawia się po dodaniu wszystkich produktów */}
          <AnimatePresence>
            {showMarkup && (
              <motion.div
                key="markup-row"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{
                  duration: 0.3,
                  ease: [0.21, 0.47, 0.32, 0.98],
                }}
                className="flex items-center justify-between text-[10px] py-1.5 border-b border-border/30"
              >
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <span className="font-medium">Narzut</span>
                  <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-primary/15 text-primary tabular-nums leading-none">
                    {MARKUP_PERCENT}%
                  </span>
                </span>
                <span className="font-medium text-primary tabular-nums flex-shrink-0">
                  +{formatPLN(MARKUP_AMOUNT)}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Total bar */}
        <div className="px-3 py-2 border-t border-border/40 bg-muted/30 flex items-center justify-between">
          <span className="text-[9px] font-medium uppercase tracking-wider text-muted-foreground">
            Suma
          </span>
          <span className="text-[14px] font-bold text-foreground tabular-nums">
            {totalDisplay}
          </span>
        </div>
      </div>
    </div>
  );
}
